import type { HazardZoneDTO, MazeDTO, WallDTO } from "../../shared/protocol/index.js";
import { state } from "../state.js";
import type { Renderer } from "./Renderer.js";
import { HAZARD_ICON, REDUCED_MOTION, type FogView } from "./types.js";
import { isPointVisible } from "./fog.js";
import { hexToRgba } from "./util.js";

function initPatterns(rd: Renderer): void {
  if (rd.brickPattern && rd.steelPattern) return;
  const { ctx } = rd;

  // Create brick canvas (8x8 pixels)
  const bCanvas = document.createElement("canvas");
  bCanvas.width = 8;
  bCanvas.height = 8;
  const bCtx = bCanvas.getContext("2d")!;
  bCtx.fillStyle = "#a83800"; // Red-brown bricks
  bCtx.fillRect(0, 0, 8, 8);
  bCtx.fillStyle = "#f8b800"; // Retro yellowish-orange mortar
  bCtx.fillRect(0, 7, 8, 1);
  bCtx.fillRect(7, 0, 1, 8);
  bCtx.fillRect(3, 0, 1, 4);
  rd.brickPattern = ctx.createPattern(bCanvas, "repeat");

  // Create steel canvas (16x16 pixels)
  const sCanvas = document.createElement("canvas");
  sCanvas.width = 16;
  sCanvas.height = 16;
  const sCtx = sCanvas.getContext("2d")!;
  sCtx.fillStyle = "#b8b8b8"; // Light grey steel blocks
  sCtx.fillRect(0, 0, 16, 16);
  sCtx.fillStyle = "#ffffff"; // Highlights
  sCtx.fillRect(0, 0, 16, 1);
  sCtx.fillRect(0, 0, 1, 16);
  sCtx.fillStyle = "#000000"; // Shadows
  sCtx.fillRect(0, 15, 16, 1);
  sCtx.fillRect(15, 0, 1, 16);
  sCtx.fillStyle = "#808080"; // Inset grey
  sCtx.fillRect(2, 2, 12, 12);
  sCtx.fillStyle = "#b8b8b8";
  sCtx.fillRect(4, 4, 8, 8);
  rd.steelPattern = ctx.createPattern(sCanvas, "repeat");
}

export function drawFloor(rd: Renderer, maze: MazeDTO): void {
  const { ctx } = rd;
  if (state.realisticEnabled) {
    // Terrain detail (rocks, ruts, tufts, vignette) is static per maze —
    // pre-rendered once, like the paper floor.
    const key = `real:${maze.width}x${maze.height}@${rd.dpr}`;
    if (!rd.floorCache || rd.floorCacheKey !== key) {
      rd.floorCache = buildRealisticFloor(maze.width, maze.height, rd.dpr);
      rd.floorCacheKey = key;
    }
    ctx.drawImage(rd.floorCache, 0, 0, maze.width, maze.height);
    return;
  }

  if (state.modernEnabled) {
    const key = `modern:${maze.width}x${maze.height}@${rd.dpr}`;
    if (!rd.floorCache || rd.floorCacheKey !== key) {
      rd.floorCache = buildModernFloor(maze.width, maze.height, rd.dpr);
      rd.floorCacheKey = key;
    }
    ctx.drawImage(rd.floorCache, 0, 0, maze.width, maze.height);
    return;
  }

  if (state.battleCityEnabled) {
    // Near-black NES playfield with baked-in tile seams and pixel rubble.
    const key = `bc:${maze.width}x${maze.height}@${rd.dpr}`;
    if (!rd.floorCache || rd.floorCacheKey !== key) {
      rd.floorCache = buildBattleCityFloor(maze.width, maze.height, rd.dpr);
      rd.floorCacheKey = key;
    }
    ctx.drawImage(rd.floorCache, 0, 0, maze.width, maze.height);
  } else {
    // The paper floor is static per maze, and its hand-colored detail
    // (watercolor washes, graph ruling, speckles, map decor) is too much to
    // redraw at 60 fps — render it once to an offscreen canvas and blit.
    const key = `paper:${maze.width}x${maze.height}@${rd.dpr}`;
    if (!rd.floorCache || rd.floorCacheKey !== key) {
      rd.floorCache = buildPaperFloor(maze.width, maze.height, rd.dpr);
      rd.floorCacheKey = key;
    }
    ctx.drawImage(rd.floorCache, 0, 0, maze.width, maze.height);
  }
}

export function drawMapFeatures(rd: Renderer, nowMs: number, fog: FogView | null): void {
  const { ctx } = rd;
  // Team spawn zones: a faint wash of each team's color, with a soft dashed
  // outline. Drawn under the walls so the arena structure reads on top.
  for (const z of rd.spawnZones) {
    ctx.fillStyle = hexToRgba(z.color, 0.18);
    ctx.fillRect(z.x, z.y, z.width, z.height);
    ctx.save();
    ctx.strokeStyle = hexToRgba(z.color, 0.55);
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 5]);
    ctx.strokeRect(z.x + 1, z.y + 1, z.width - 2, z.height - 2);
    ctx.restore();
  }

  // Hazard zones: animated terrain tints, drawn under the walls.
  for (const h of rd.hazards) {
    if (fog && !isRectVisible(h.x, h.y, h.width, h.height, fog)) continue;
    drawHazard(rd, h, nowMs);
  }
}

export function drawWalls(rd: Renderer, maze: MazeDTO, nowMs?: number): void {
  const { ctx } = rd;
  if (state.realisticEnabled) {
    // Extruded concrete slabs, sun from the top-left (matching floor/tank
    // shadows): ground shadow → dark side face → top slab → sunlit bevel,
    // finished with expansion-joint ticks across the slab.
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const trace = (dx: number, dy: number) => {
      ctx.beginPath();
      for (let i = 0; i < maze.walls.length; i++) {
        if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
        const w = maze.walls[i];
        ctx.moveTo(w.x1 + dx, w.y1 + dy);
        ctx.lineTo(w.x2 + dx, w.y2 + dy);
      }
    };

    trace(3, 4);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.32)";
    ctx.lineWidth = maze.thickness + 3;
    ctx.stroke();

    trace(2, 2.5);
    ctx.strokeStyle = "#41403a";
    ctx.lineWidth = maze.thickness;
    ctx.stroke();

    trace(0, 0);
    ctx.strokeStyle = "#616156";
    ctx.lineWidth = maze.thickness;
    ctx.stroke();

    trace(-1, -1.5);
    ctx.strokeStyle = "rgba(196, 190, 168, 0.30)";
    ctx.lineWidth = Math.max(1.5, maze.thickness - 5);
    ctx.stroke();

    // Expansion joints: faint perpendicular ticks along each slab.
    ctx.strokeStyle = "rgba(0, 0, 0, 0.16)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i < maze.walls.length; i++) {
      if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
      const w = maze.walls[i];
      const dx = w.x2 - w.x1;
      const dy = w.y2 - w.y1;
      const len = Math.hypot(dx, dy);
      if (len < 30) continue;
      const ux = dx / len;
      const uy = dy / len;
      const px = -uy * (maze.thickness / 2 - 1);
      const py = ux * (maze.thickness / 2 - 1);
      for (let d = 30; d < len - 4; d += 30) {
        const jx = w.x1 + ux * d;
        const jy = w.y1 + uy * d;
        ctx.moveTo(jx - px, jy - py);
        ctx.lineTo(jx + px, jy + py);
      }
    }
    ctx.stroke();

    drawRealisticWallDamage(rd, maze);
    ctx.restore();
    return;
  }

  if (state.modernEnabled) {
    const now = nowMs ?? performance.now();
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const intactIndices: number[] = [];
    const crackedIndices: number[] = [];
    const failingIndices: number[] = [];
    for (let i = 0; i < maze.walls.length; i++) {
      if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
      const tier = wallDamageTier(rd, i);
      if (tier === 2) failingIndices.push(i);
      else if (tier === 1) crackedIndices.push(i);
      else intactIndices.push(i);
    }

    const trace = (indices: number[]) => {
      ctx.beginPath();
      for (const idx of indices) {
        const w = maze.walls[idx];
        ctx.moveTo(w.x1, w.y1);
        ctx.lineTo(w.x2, w.y2);
      }
    };

    // Draw intact walls
    if (intactIndices.length > 0) {
      // 1. Light spill (widest, first)
      trace(intactIndices);
      ctx.strokeStyle = "rgba(0, 248, 248, 0.06)";
      ctx.lineWidth = maze.thickness + 14;
      ctx.stroke();

      // 2. Dark slab body: solid #111827
      ctx.strokeStyle = "#111827";
      ctx.lineWidth = maze.thickness;
      ctx.stroke();

      // 3. Slab edge: #1e293b
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = Math.max(1, maze.thickness - 4);
      ctx.stroke();

      // 4. Thin emissive halo hugging the wire — keeps the slab body dark
      // so walls read as physical barriers, not light strips.
      ctx.strokeStyle = "rgba(0, 248, 248, 0.4)";
      ctx.lineWidth = 4.5;
      ctx.stroke();

      // 5. Bright white center wire
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // 6. Energy pulse dash
      if (!REDUCED_MOTION) {
        ctx.strokeStyle = "rgba(0, 248, 248, 0.6)";
        ctx.lineWidth = 2.5;
        ctx.setLineDash([26, 18]);
        ctx.lineDashOffset = -(now / 40) % 44;
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // Cracked walls (light damage): the field still holds, but the wire runs
    // dim and unsteady — no energy pulse, halved halo.
    if (crackedIndices.length > 0) {
      trace(crackedIndices);

      const wobble = REDUCED_MOTION ? 0.9 : 0.8 + 0.2 * Math.sin(now / 160);
      ctx.strokeStyle = `rgba(0, 248, 248, ${0.06 * wobble})`;
      ctx.lineWidth = maze.thickness + 14;
      ctx.stroke();

      ctx.strokeStyle = "#111827";
      ctx.lineWidth = maze.thickness;
      ctx.stroke();

      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = Math.max(1, maze.thickness - 4);
      ctx.stroke();

      ctx.strokeStyle = "rgba(0, 248, 248, 0.2)";
      ctx.lineWidth = 4.5;
      ctx.stroke();

      ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Failing walls (heavy damage): the wire is dead except for rare sputters,
    // leaving a dark slab with a guttering bloom.
    if (failingIndices.length > 0) {
      trace(failingIndices);

      // 1. Flickering bloom
      const flicker = 0.5 + 0.5 * Math.sin(now / 90);
      ctx.strokeStyle = `rgba(0, 248, 248, ${0.06 * flicker})`;
      ctx.lineWidth = maze.thickness + 14;
      ctx.stroke();

      // 2. Dark slab body
      ctx.strokeStyle = "#111827";
      ctx.lineWidth = maze.thickness;
      ctx.stroke();

      // 3. Slab edge
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = Math.max(1, maze.thickness - 4);
      ctx.stroke();

      // 4. Dying wire: sputters back on in brief, irregular bursts.
      if (!REDUCED_MOTION) {
        const sputter = Math.max(0, Math.sin(now / 70) * Math.sin(now / 233) - 0.55) / 0.45;
        if (sputter > 0) {
          ctx.strokeStyle = `rgba(0, 248, 248, ${0.5 * sputter})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }

    ctx.restore();
    return;
  }

  if (state.battleCityEnabled) {
    initPatterns(rd);
    const thickness = maze.thickness;
    const rect = (w: WallDTO) => ({
      x: Math.min(w.x1, w.x2) - thickness / 2,
      y: Math.min(w.y1, w.y2) - thickness / 2,
      width: Math.abs(w.x2 - w.x1) + thickness,
      height: Math.abs(w.y2 - w.y1) + thickness,
    });

    // Pass 1: hard offset drop shadows (all walls, so fills paint over any
    // shadow that lands on a neighboring wall).
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    for (let i = 0; i < maze.walls.length; i++) {
      if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
      const { x, y, width, height } = rect(maze.walls[i]);
      ctx.fillRect(x + 3, y + 3, width, height);
    }

    // Pass 2: pattern fills + hard 8-bit bevel (lit top/left, dark bottom/right).
    for (let i = 0; i < maze.walls.length; i++) {
      if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
      const w = maze.walls[i];

      // Boundaries are steel, interior walls are destructible bricks
      const isBorder = w.x1 === 0 || w.y1 === 0 || w.x2 === maze.width || w.y2 === maze.height;
      const isDestructible = rd.destructibleWalls && !isBorder;

      // Fill rectangles for clean alignment of repeating pixel patterns
      const { x, y, width, height } = rect(w);
      ctx.fillStyle = (isDestructible ? rd.brickPattern : rd.steelPattern) || "#352f25";
      ctx.fillRect(x, y, width, height);

      ctx.fillStyle = "rgba(255, 255, 255, 0.22)";
      ctx.fillRect(x, y, width, 2);
      ctx.fillRect(x, y, 2, height);
      ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
      ctx.fillRect(x, y + height - 2, width, 2);
      ctx.fillRect(x + width - 2, y, 2, height);
    }

    // Pass 3: shot-up bricks lose grid-aligned chunks down to the black floor,
    // like the half-eaten brick tiles in the original game.
    for (let i = 0; i < maze.walls.length; i++) {
      const tier = wallDamageTier(rd, i);
      if (tier === 0) continue;
      const rand = damageRand(i, rd.wallHp[i]);
      const { x, y, width, height } = rect(maze.walls[i]);
      const bites = Math.max(2, Math.round((width * height) / 220)) * tier;
      ctx.fillStyle = "#000000";
      for (let b = 0; b < bites; b++) {
        const s = tier === 2 && rand() < 0.4 ? 8 : 4;
        const bx = x + Math.floor((rand() * Math.max(1, width - s)) / 4) * 4;
        const by = y + Math.floor((rand() * Math.max(1, height - s)) / 4) * 4;
        ctx.fillRect(bx, by, s, s);
      }
      if (tier === 2) {
        // Loose mortar flecks scattered in the craters.
        ctx.fillStyle = "#f8b800";
        const flecks = Math.max(2, Math.round((width * height) / 800));
        for (let f = 0; f < flecks; f++) {
          ctx.fillRect(
            x + Math.floor(rand() * Math.max(1, width - 2)),
            y + Math.floor(rand() * Math.max(1, height - 2)),
            2, 2
          );
        }
      }
    }
    return;
  }

  // Default Tanka Maze wall drawing code:
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // 1. Soft graphite pencil drop shadow for depth
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i < maze.walls.length; i++) {
    if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
    const w = maze.walls[i];
    ctx.moveTo(w.x1 + 3, w.y1 + 4);
    ctx.lineTo(w.x2 + 3, w.y2 + 4);
  }
  ctx.strokeStyle = "rgba(70, 55, 30, 0.14)"; // Warm organic shadow
  ctx.lineWidth = maze.thickness;
  ctx.stroke();
  ctx.restore();

  // 2. Faint sketch backing line
  ctx.beginPath();
  for (let i = 0; i < maze.walls.length; i++) {
    if (rd.destructibleWalls && rd.wallHp[i] <= 0) continue;
    const w = maze.walls[i];
    ctx.moveTo(w.x1, w.y1);
    ctx.lineTo(w.x2, w.y2);
  }
  ctx.strokeStyle = "rgba(228,214,176,0.5)";
  ctx.lineWidth = maze.thickness + 3;
  ctx.stroke();

  // 3. Final hand-drawn main ink stroke
  ctx.strokeStyle = "#352f25";
  ctx.lineWidth = maze.thickness;
  ctx.stroke();

  // 4. Warm sienna marker fill inside the ink edges, so walls read as
  // colored-in wood rather than flat black bars.
  ctx.strokeStyle = "rgba(146, 94, 52, 0.55)";
  ctx.lineWidth = Math.max(1.5, maze.thickness - 4);
  ctx.stroke();

  drawPaperWallDamage(rd, maze);
}

/** Per-wall damage tier: 0 = intact, 1 = cracked (above half HP), 2 = crumbling. */
function wallDamageTier(rd: Renderer, i: number): 0 | 1 | 2 {
  if (!rd.destructibleWalls) return 0;
  const hp = rd.wallHp[i];
  if (!(hp > 0) || hp >= rd.wallMaxHp) return 0;
  return hp > rd.wallMaxHp / 2 ? 1 : 2;
}

/** Deterministic RNG for damage decals, reseeded per HP step so every hit
 * visibly chews the wall in a new place without flickering between frames. */
function damageRand(i: number, hp: number): () => number {
  return mulberry32((((i + 1) * 2654435761) ^ (hp * 7919)) >>> 0);
}

/** Append jagged crack polylines crossing the wall to the current path. */
function traceCracks(
  ctx: CanvasRenderingContext2D,
  w: WallDTO,
  thickness: number,
  count: number,
  rand: () => number
): void {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const half = thickness / 2;
  for (let c = 0; c < count; c++) {
    const at = len * (0.12 + rand() * 0.76);
    ctx.moveTo(w.x1 + ux * at + px * half, w.y1 + uy * at + py * half);
    const segs = 3;
    for (let s = 1; s <= segs; s++) {
      const along = at + (rand() - 0.5) * thickness;
      const across = half - thickness * (s / segs);
      ctx.lineTo(w.x1 + ux * along + px * across, w.y1 + uy * along + py * across);
    }
  }
}

/** Fill rounded bites eaten out of the wall's long edges. */
function drawEdgeChips(
  ctx: CanvasRenderingContext2D,
  w: WallDTO,
  thickness: number,
  count: number,
  size: number,
  color: string,
  rand: () => number
): void {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = (-uy * thickness) / 2;
  const py = (ux * thickness) / 2;
  ctx.fillStyle = color;
  for (let c = 0; c < count; c++) {
    const at = len * (0.1 + rand() * 0.8);
    const side = rand() < 0.5 ? 1 : -1;
    ctx.beginPath();
    ctx.arc(
      w.x1 + ux * at + px * side,
      w.y1 + uy * at + py * side,
      size * (0.6 + rand() * 0.8),
      0,
      Math.PI * 2
    );
    ctx.fill();
  }
}

/** Paper skin: damaged walls pick up inked cracks; crumbling ones fade to a
 * chalky wash with paper-colored bites out of the ink edges. */
function drawPaperWallDamage(rd: Renderer, maze: MazeDTO): void {
  const { ctx } = rd;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (let i = 0; i < maze.walls.length; i++) {
    const tier = wallDamageTier(rd, i);
    if (tier === 0) continue;
    const w = maze.walls[i];
    const rand = damageRand(i, rd.wallHp[i]);
    const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;

    if (tier === 2) {
      // Worn, chalky wash over the marker fill so the wall reads battered.
      ctx.beginPath();
      ctx.moveTo(w.x1, w.y1);
      ctx.lineTo(w.x2, w.y2);
      ctx.strokeStyle = "rgba(228, 214, 176, 0.4)";
      ctx.lineWidth = Math.max(1.5, maze.thickness - 4);
      ctx.stroke();

      drawEdgeChips(
        ctx, w, maze.thickness,
        Math.max(2, Math.round(len / 45)),
        maze.thickness * 0.3,
        "rgba(233, 219, 185, 0.9)",
        rand
      );
    }

    // Hand-inked cracks across the wood.
    ctx.beginPath();
    traceCracks(ctx, w, maze.thickness, Math.max(1, Math.round(len / 60)) * tier, rand);
    ctx.strokeStyle = "rgba(36, 31, 22, 0.75)";
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }
  ctx.restore();
}

/** Realistic skin: cracks spider across the slab; crumbling walls spall dark
 * chunks off their edges and shed rubble onto the sand beside them. */
function drawRealisticWallDamage(rd: Renderer, maze: MazeDTO): void {
  const { ctx } = rd;
  ctx.save();
  ctx.lineCap = "butt";
  ctx.lineJoin = "round";
  for (let i = 0; i < maze.walls.length; i++) {
    const tier = wallDamageTier(rd, i);
    if (tier === 0) continue;
    const w = maze.walls[i];
    const rand = damageRand(i, rd.wallHp[i]);
    const dx = w.x2 - w.x1;
    const dy = w.y2 - w.y1;
    const len = Math.hypot(dx, dy) || 1;

    if (tier === 2) {
      // Spalled edges: notches down to the dark side-face color.
      drawEdgeChips(
        ctx, w, maze.thickness,
        Math.max(2, Math.round(len / 40)),
        maze.thickness * 0.28,
        "rgba(46, 44, 37, 0.85)",
        rand
      );

      // Rubble shed onto the ground alongside the slab.
      const ux = dx / len;
      const uy = dy / len;
      ctx.fillStyle = "rgba(40, 36, 26, 0.5)";
      const rubble = Math.max(3, Math.round(len / 30));
      for (let r = 0; r < rubble; r++) {
        const at = len * rand();
        const side = rand() < 0.5 ? 1 : -1;
        const off = maze.thickness / 2 + 2 + rand() * 5;
        ctx.fillRect(
          w.x1 + ux * at - uy * off * side - 1,
          w.y1 + uy * at + ux * off * side - 1,
          2, 2
        );
      }
    }

    ctx.beginPath();
    traceCracks(ctx, w, maze.thickness, Math.max(1, Math.round(len / 55)) * tier, rand);
    ctx.strokeStyle = "rgba(15, 14, 10, 0.55)";
    ctx.lineWidth = 1.3;
    ctx.stroke();
  }
  ctx.restore();
}

export function isRectVisible(x: number, y: number, width: number, height: number, fog: FogView): boolean {
  const points = [
    [x + width / 2, y + height / 2],
    [x, y],
    [x + width, y],
    [x, y + height],
    [x + width, y + height],
  ] as const;
  return points.some(([px, py]) => isPointVisible(px, py, fog));
}

/** Draw one hazard-zone tile (lava/mud/ice/heal), with artwork matched to the
 *  active skin: inked map terrain on paper, chunky NES floor tiles for Battle
 *  City, holographic zone projections for Modern, and painted terrain for
 *  Realistic. Dispatch order mirrors drawFloor/drawWalls. */
function drawHazard(rd: Renderer, h: HazardZoneDTO, nowMs: number): void {
  const { ctx } = rd;

  ctx.save();
  // Clip drawing to the hazard zone rectangle so textures stay within bounds
  ctx.beginPath();
  ctx.rect(h.x, h.y, h.width, h.height);
  ctx.clip();

  if (state.realisticEnabled) drawHazardRealistic(ctx, h, nowMs);
  else if (state.modernEnabled) drawHazardModern(ctx, h, nowMs);
  else if (state.battleCityEnabled) drawHazardBattleCity(ctx, h, nowMs);
  else drawHazardPaper(ctx, h, nowMs);

  ctx.restore();
}

/** Stamp the hazard's glyph optically centered in the zone. */
function stampHazardGlyph(
  ctx: CanvasRenderingContext2D,
  h: HazardZoneDTO,
  font: string,
  fillStyle: string
): void {
  ctx.save();
  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = fillStyle;
  const glyph = HAZARD_ICON[h.type].glyph;
  const m = ctx.measureText(glyph);
  const dx = (m.actualBoundingBoxLeft - m.actualBoundingBoxRight) / 2;
  const dy = (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
  ctx.fillText(glyph, h.x + h.width / 2 + dx, h.y + h.height / 2 + dy);
  ctx.restore();
}

/** Realistic skin: painted, physical terrain — magma gradients, viscous mud,
 *  glinting ice, a glowing energy pool — under a stenciled white glyph. */
function drawHazardRealistic(ctx: CanvasRenderingContext2D, h: HazardZoneDTO, nowMs: number): void {
  const { x, y, width, height, type } = h;

  if (type === "lava") {
    // 1. Base magma background gradient
    const grad = ctx.createLinearGradient(x, y, x + width, y + height);
    const shift = Math.sin(nowMs / 1200) * 0.15;
    grad.addColorStop(0, hexToRgba("#a82000", 0.7)); // Dark crimson
    grad.addColorStop(0.5 + shift, hexToRgba("#e85a0c", 0.75)); // Bright orange
    grad.addColorStop(1, hexToRgba("#ffba08", 0.7)); // Yellow-gold
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, width, height);

    // 2. Animated magma convection cells/veins
    ctx.strokeStyle = "rgba(255, 120, 0, 0.4)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(x, y + height * 0.3 + Math.sin(nowMs / 300) * 4);
    ctx.bezierCurveTo(
      x + width * 0.3, y + height * 0.1 + Math.cos(nowMs / 400) * 6,
      x + width * 0.7, y + height * 0.5 + Math.sin(nowMs / 500) * 6,
      x + width, y + height * 0.3
    );
    ctx.stroke();

    // 3. Magma bubble blobs pulsing
    const numBlobs = 3;
    ctx.fillStyle = "rgba(255, 200, 0, 0.25)";
    for (let i = 0; i < numBlobs; i++) {
      const bx = x + ((x * (i + 1) + 17) % (width - 20)) + 10;
      const by = y + ((y * (i + 3) + 31) % (height - 20)) + 10;
      const sizeScale = 0.6 + 0.4 * Math.sin(nowMs / 400 + i * 2.3);
      const radius = 6 * sizeScale;
      ctx.beginPath();
      ctx.arc(bx, by, radius, 0, Math.PI * 2);
      ctx.fill();
    }

  } else if (type === "mud") {
    // 1. Thick viscous mud background
    ctx.fillStyle = "rgba(75, 48, 25, 0.8)";
    ctx.fillRect(x, y, width, height);

    // 2. Slow organic swirls
    ctx.strokeStyle = "rgba(55, 34, 16, 0.5)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x + width / 2, y + height / 2, Math.min(width, height) * 0.25 + Math.sin(nowMs / 800) * 2, 0, Math.PI, false);
    ctx.stroke();

    // 3. Bubbles popping (bursting circles)
    const numMudBubbles = 2;
    for (let i = 0; i < numMudBubbles; i++) {
      const bx = x + ((x * (i + 5) + 43) % (width - 16)) + 8;
      const by = y + ((y * (i + 2) + 79) % (height - 16)) + 8;
      const phase = (nowMs / 1200 + i * 0.7) % 1.0; // 0 to 1
      const radius = 2 + 6 * phase;
      ctx.beginPath();
      ctx.arc(bx, by, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(110, 80, 50, ${0.8 * (1 - phase)})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

  } else if (type === "ice") {
    // 1. Cool ice crystal gradient
    const grad = ctx.createRadialGradient(
      x + width / 2, y + height / 2, 2,
      x + width / 2, y + height / 2, Math.max(width, height)
    );
    grad.addColorStop(0, "rgba(220, 245, 255, 0.8)"); // Bright frost center
    grad.addColorStop(1, "rgba(80, 140, 185, 0.75)");  // Deep ice blue edge
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, width, height);

    // 2. Specular diagonal ice shine/glint
    const glintShift = ((nowMs / 15) % (width * 3)) - width;
    ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
    ctx.beginPath();
    ctx.moveTo(x + glintShift, y);
    ctx.lineTo(x + glintShift + 12, y);
    ctx.lineTo(x + glintShift + 12 - height, y + height);
    ctx.lineTo(x + glintShift - height, y + height);
    ctx.fill();

    // 3. Diagonal frost crack lines
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + width * 0.15, y + height * 0.1);
    ctx.lineTo(x + width * 0.4, y + height * 0.5);
    ctx.lineTo(x + width * 0.35, y + height * 0.8);
    ctx.moveTo(x + width * 0.85, y + height * 0.2);
    ctx.lineTo(x + width * 0.6, y + height * 0.75);
    ctx.stroke();

  } else if (type === "heal") {
    // 1. Soothing green energy pool background
    const grad = ctx.createRadialGradient(
      x + width / 2, y + height / 2, 2,
      x + width / 2, y + height / 2, Math.max(width, height) * 0.6
    );
    grad.addColorStop(0, "rgba(100, 230, 140, 0.6)"); // Healing core
    grad.addColorStop(1, "rgba(45, 140, 60, 0.45)");   // Forest green edge
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, width, height);

    // 2. Concentric healing ripples pulsing outward
    ctx.strokeStyle = "rgba(200, 255, 220, 0.3)";
    ctx.lineWidth = 2;
    const ripplePhase = (nowMs / 1000) % 1.0;
    ctx.beginPath();
    ctx.arc(x + width / 2, y + height / 2, Math.min(width, height) * 0.45 * ripplePhase, 0, Math.PI * 2);
    ctx.stroke();

    // 3. Floating healing '+' particles drifting upwards
    ctx.fillStyle = "rgba(140, 255, 170, 0.7)";
    const numCrosses = 3;
    for (let i = 0; i < numCrosses; i++) {
      const seedX = (x * (i + 3) + 71) % (width - 14) + 7;
      const seedY = height - ((nowMs / 18 + i * 25) % height); // Drifts upward!
      const px = x + seedX;
      const py = y + seedY;
      const size = 3;
      ctx.fillRect(px - size, py - 1, size * 2, 2);
      ctx.fillRect(px - 1, py - size, 2, size * 2);
    }
  }

  // 4. Subtle, high-contrast mechanical icon overlay at the center
  const pulse = 0.28 + 0.1 * Math.abs(Math.sin(nowMs / 400));
  stampHazardGlyph(
    ctx, h,
    `bold ${Math.min(width, height) * 0.5}px sans-serif`,
    hexToRgba("#ffffff", pulse * 0.6) // White overlay stamp for maximum readability
  );

  // 5. Border dash outline
  ctx.strokeStyle = hexToRgba(HAZARD_ICON[type].color, 0.4);
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 4]);
  ctx.strokeRect(x + 1, y + 1, width - 2, height - 2);
  ctx.setLineDash([]);
}

// Paper skin: base wash pigment per hazard, echoed by its ink symbols.
const PAPER_HAZARD_WASH: Record<HazardZoneDTO["type"], string> = {
  lava: "#c2452f",
  mud: "#8a6b3f",
  ice: "#79b6cc",
  heal: "#58b083",
};
const PAPER_HAZARD_INK: Record<HazardZoneDTO["type"], string> = {
  lava: "rgba(140, 45, 25, 0.60)",
  mud: "rgba(80, 58, 30, 0.60)",
  ice: "rgba(50, 90, 110, 0.55)",
  heal: "rgba(35, 110, 70, 0.60)",
};

/** Paper skin: hazards are hand-annotated map regions — a watercolor wash
 *  drying unevenly, cartographic ink symbols (flames, marsh tufts, snowflake
 *  asterisks, crosses), and a wobbly double-inked border. */
function drawHazardPaper(ctx: CanvasRenderingContext2D, h: HazardZoneDTO, nowMs: number): void {
  const { x, y, width, height, type } = h;
  // Seeded per zone so the hand-drawn jitter is stable frame to frame.
  const rand = mulberry32((Math.round(x) * 7919 + Math.round(y) * 104729) >>> 0);
  const breathe = REDUCED_MOTION ? 1 : 0.88 + 0.12 * Math.sin(nowMs / 1300);

  // 1. Watercolor wash: flat tint plus two soft blotches of pooled pigment.
  const wash = PAPER_HAZARD_WASH[type];
  ctx.fillStyle = hexToRgba(wash, 0.24 * breathe);
  ctx.fillRect(x, y, width, height);
  for (let i = 0; i < 2; i++) {
    const bx = x + width * (0.25 + rand() * 0.5);
    const by = y + height * (0.25 + rand() * 0.5);
    const r = Math.min(width, height) * (0.3 + rand() * 0.25);
    const g = ctx.createRadialGradient(bx, by, r * 0.2, bx, by, r);
    g.addColorStop(0, hexToRgba(wash, 0.14));
    g.addColorStop(1, hexToRgba(wash, 0));
    ctx.fillStyle = g;
    ctx.fillRect(x, y, width, height);
  }

  // 2. Cartographic terrain symbols in ink, scattered on a loose offset grid.
  ctx.save();
  ctx.strokeStyle = PAPER_HAZARD_INK[type];
  ctx.lineWidth = 1.4;
  ctx.lineCap = "round";
  const step = 26;
  let row = 0;
  for (let sy = y + 12; sy < y + height - 4; sy += step, row++) {
    for (let sx = x + 12 + (row % 2) * (step / 2); sx < x + width - 6; sx += step) {
      const jx = sx + (rand() - 0.5) * 8;
      const jy = sy + (rand() - 0.5) * 8;
      ctx.beginPath();
      if (type === "lava") {
        // Little inked flame: a peaked stroke with a flicked inner tongue.
        ctx.moveTo(jx - 3.5, jy + 3);
        ctx.lineTo(jx, jy - 4.5);
        ctx.lineTo(jx + 3.5, jy + 3);
        ctx.moveTo(jx, jy + 2);
        ctx.lineTo(jx, jy - 1);
      } else if (type === "mud") {
        // Classic marsh symbol: baseline with grass tufts rising from it.
        ctx.moveTo(jx - 5, jy + 2);
        ctx.lineTo(jx + 5, jy + 2);
        ctx.moveTo(jx - 2, jy + 2);
        ctx.lineTo(jx - 3, jy - 2);
        ctx.moveTo(jx, jy + 2);
        ctx.lineTo(jx, jy - 3.5);
        ctx.moveTo(jx + 2, jy + 2);
        ctx.lineTo(jx + 3, jy - 2);
      } else if (type === "ice") {
        // Snow asterisk: three strokes crossing at 60 degrees.
        for (let a = 0; a < 3; a++) {
          const ang = (Math.PI / 3) * a + Math.PI / 6;
          ctx.moveTo(jx - Math.cos(ang) * 4, jy - Math.sin(ang) * 4);
          ctx.lineTo(jx + Math.cos(ang) * 4, jy + Math.sin(ang) * 4);
        }
      } else {
        // Small apothecary cross.
        ctx.moveTo(jx - 3.5, jy);
        ctx.lineTo(jx + 3.5, jy);
        ctx.moveTo(jx, jy - 3.5);
        ctx.lineTo(jx, jy + 3.5);
      }
      ctx.stroke();
    }
  }
  ctx.restore();

  // 3. Wobbly double-inked border, like a region traced by hand twice.
  ctx.save();
  ctx.strokeStyle = "rgba(58, 47, 30, 0.5)";
  ctx.lineWidth = 1.2;
  ctx.lineJoin = "round";
  for (let pass = 0; pass < 2; pass++) {
    const inset = 2 + pass * 2.5;
    ctx.beginPath();
    const corners = [
      [x + inset, y + inset],
      [x + width - inset, y + inset],
      [x + width - inset, y + height - inset],
      [x + inset, y + height - inset],
    ];
    for (let e = 0; e < 4; e++) {
      const [x1, y1] = corners[e];
      const [x2, y2] = corners[(e + 1) % 4];
      ctx.moveTo(x1, y1);
      const segs = Math.max(2, Math.round(Math.hypot(x2 - x1, y2 - y1) / 16));
      for (let s = 1; s <= segs; s++) {
        const t = s / segs;
        ctx.lineTo(
          x1 + (x2 - x1) * t + (rand() - 0.5) * 2.2,
          y1 + (y2 - y1) * t + (rand() - 0.5) * 2.2
        );
      }
    }
    ctx.stroke();
  }
  ctx.restore();

  // 4. Glyph stamped like a map legend mark, in the terrain's ink.
  stampHazardGlyph(
    ctx, h,
    `600 ${Math.min(width, height) * 0.42}px Georgia, "Times New Roman", serif`,
    hexToRgba(wash, 0.4 * breathe + 0.12)
  );
}

// Battle City skin: NES-style palettes and 7x7 pixel icons per hazard.
const BC_HAZARD_BASE: Record<HazardZoneDTO["type"], string> = {
  lava: "#7c0800",
  mud: "#3a2a14",
  ice: "#a0d8e8",
  heal: "#0c2c0c",
};
const BC_HAZARD_ICON: Record<HazardZoneDTO["type"], string[]> = {
  lava: [
    "...X...",
    "..XX...",
    "..XXX..",
    ".XXXX..",
    "XXXXXX.",
    "XX.XXXX",
    ".XXXXX.",
  ],
  mud: [
    ".......",
    "XX..XX.",
    "..XX..X",
    ".......",
    ".XX..XX",
    "X..XX..",
    ".......",
  ],
  ice: [
    "...X...",
    ".X.X.X.",
    "..XXX..",
    "XXXXXXX",
    "..XXX..",
    ".X.X.X.",
    "...X...",
  ],
  heal: [
    "..XXX..",
    "..XXX..",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "..XXX..",
    "..XXX..",
  ],
};

/** Battle City skin: hazards are flat NES floor tiles — 4px pixel patterns in
 *  a tiny palette, animated by flipping between two frames like the original
 *  game's water, with a chunky pixel icon stamped in the middle. */
function drawHazardBattleCity(ctx: CanvasRenderingContext2D, h: HazardZoneDTO, nowMs: number): void {
  const { x, y, width, height, type } = h;
  const P = 4; // NES "pixel" size, matching the wall damage grid
  const frame = REDUCED_MOTION ? 0 : Math.floor(nowMs / 480) % 2;

  ctx.fillStyle = BC_HAZARD_BASE[type];
  ctx.fillRect(x, y, width, height);

  // Pattern cells on a world-aligned 4px grid; a cheap integer hash keeps the
  // speckle deterministic so tiles don't crawl between frames.
  const x0 = Math.floor(x / P) * P;
  const y0 = Math.floor(y / P) * P;
  for (let cy = y0; cy < y + height; cy += P) {
    for (let cx = x0; cx < x + width; cx += P) {
      const i = cx / P;
      const j = cy / P;
      const n = ((i * 73856093) ^ (j * 19349663)) >>> 0;
      const r = (n % 97) / 97;
      let c: string | null = null;
      if (type === "lava") {
        // Shimmering magma bands, alternating with the frame flip.
        if ((j + frame * 3) % 6 === 0) c = "#5c0800";
        else if ((i + j * 3 + frame * 2) % 7 === 0) c = "#e45c10";
        else if (n % 43 === 0 && frame === 1) c = "#f8b800";
      } else if (type === "mud") {
        if (r < 0.16) c = "#241a0c";
        else if (r > 0.94) c = "#5c4424";
      } else if (type === "ice") {
        // Diagonal white glints slide one step on the frame flip.
        if ((((i - j + frame) % 9) + 9) % 9 === 0) c = "#f8f8f8";
        else if (r < 0.14) c = "#c8ecf8";
        else if (r > 0.92) c = "#70a8c0";
      } else {
        if (r < 0.1) c = frame === 1 ? "#58f858" : "#00b800";
      }
      if (c) {
        ctx.fillStyle = c;
        ctx.fillRect(cx, cy, P, P);
      }
    }
  }

  // Hard 8-bit frame around the zone.
  ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
  ctx.fillRect(x, y, width, 2);
  ctx.fillRect(x, y + height - 2, width, 2);
  ctx.fillRect(x, y, 2, height);
  ctx.fillRect(x + width - 2, y, 2, height);

  // Chunky pixel icon, black drop shadow then white, centered on the grid.
  const icon = BC_HAZARD_ICON[type];
  const cell = P;
  const iw = icon[0].length * cell;
  const ih = icon.length * cell;
  const ix = Math.round((x + (width - iw) / 2) / P) * P;
  const iy = Math.round((y + (height - ih) / 2) / P) * P;
  for (const [color, ox, oy] of [["rgba(0, 0, 0, 0.7)", cell / 2, cell / 2], ["rgba(248, 248, 248, 0.85)", 0, 0]] as const) {
    ctx.fillStyle = color;
    for (let ry = 0; ry < icon.length; ry++) {
      for (let rx = 0; rx < icon[ry].length; rx++) {
        if (icon[ry][rx] === "X") ctx.fillRect(ix + rx * cell + ox, iy + ry * cell + oy, cell, cell);
      }
    }
  }
}

// Modern skin: emissive accent per hazard, in the HUD's neon register.
const MODERN_HAZARD_NEON: Record<HazardZoneDTO["type"], string> = {
  lava: "#ff6a00",
  mud: "#ffb020",
  ice: "#00e0ff",
  heal: "#00f890",
};

/** Modern skin: hazards are holographic zone projections — a dark glass pane
 *  with a faint neon grid, a scan sweep, corner brackets, and a glowing glyph.
 *  Matches the emissive wire language of the modern walls. */
function drawHazardModern(ctx: CanvasRenderingContext2D, h: HazardZoneDTO, nowMs: number): void {
  const { x, y, width, height, type } = h;
  const neon = MODERN_HAZARD_NEON[type];

  // 1. Dark glass pane with a soft neon core.
  ctx.fillStyle = "rgba(9, 14, 26, 0.62)";
  ctx.fillRect(x, y, width, height);
  const core = ctx.createRadialGradient(
    x + width / 2, y + height / 2, 2,
    x + width / 2, y + height / 2, Math.max(width, height) * 0.65
  );
  const corePulse = REDUCED_MOTION ? 0.12 : 0.08 + 0.07 * Math.sin(nowMs / 600);
  core.addColorStop(0, hexToRgba(neon, 0.1 + corePulse));
  core.addColorStop(1, hexToRgba(neon, 0));
  ctx.fillStyle = core;
  ctx.fillRect(x, y, width, height);

  // 2. Holographic grid lines.
  ctx.strokeStyle = hexToRgba(neon, 0.1);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let gx = x + 14; gx < x + width; gx += 14) {
    ctx.moveTo(gx, y);
    ctx.lineTo(gx, y + height);
  }
  for (let gy = y + 14; gy < y + height; gy += 14) {
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
  }
  ctx.stroke();

  // 3. Type-specific accent so zones read at a glance, not just by color.
  if (type === "mud") {
    // Diagonal caution hatching: a "slow zone" warning field.
    ctx.strokeStyle = hexToRgba(neon, 0.12);
    ctx.lineWidth = 5;
    ctx.beginPath();
    for (let d = -height; d < width; d += 22) {
      ctx.moveTo(x + d, y + height);
      ctx.lineTo(x + d + height, y);
    }
    ctx.stroke();
  } else if (type === "ice") {
    // Static specular streaks on the glass.
    ctx.strokeStyle = hexToRgba("#ffffff", 0.14);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(x + width * 0.2, y + height * 0.85);
    ctx.lineTo(x + width * 0.45, y + height * 0.15);
    ctx.moveTo(x + width * 0.55, y + height * 0.9);
    ctx.lineTo(x + width * 0.72, y + height * 0.4);
    ctx.stroke();
  } else if (type === "heal" && !REDUCED_MOTION) {
    // Regeneration ping expanding from the center.
    const phase = (nowMs / 1100) % 1;
    ctx.strokeStyle = hexToRgba(neon, 0.35 * (1 - phase));
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x + width / 2, y + height / 2, Math.min(width, height) * 0.48 * phase, 0, Math.PI * 2);
    ctx.stroke();
  } else if (type === "lava" && !REDUCED_MOTION) {
    // Thermal bloom flicker.
    const flicker = 0.1 + 0.08 * Math.abs(Math.sin(nowMs / 180) * Math.sin(nowMs / 470));
    ctx.fillStyle = hexToRgba(neon, flicker);
    ctx.fillRect(x, y, width, height);
  }

  // 4. Scan sweep drifting down the pane.
  if (!REDUCED_MOTION) {
    const bandH = 18;
    const bandY = y - bandH + ((nowMs / 14) % (height + bandH * 2));
    const band = ctx.createLinearGradient(0, bandY, 0, bandY + bandH);
    band.addColorStop(0, hexToRgba(neon, 0));
    band.addColorStop(0.5, hexToRgba(neon, 0.12));
    band.addColorStop(1, hexToRgba(neon, 0));
    ctx.fillStyle = band;
    ctx.fillRect(x, bandY, width, bandH);
  }

  // 5. Border: faint frame plus marching energy dashes, like the wall pulse.
  ctx.strokeStyle = hexToRgba(neon, 0.3);
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 1.5, y + 1.5, width - 3, height - 3);
  if (!REDUCED_MOTION) {
    ctx.strokeStyle = hexToRgba(neon, 0.6);
    ctx.lineWidth = 1.5;
    ctx.setLineDash([10, 8]);
    ctx.lineDashOffset = -(nowMs / 30) % 18;
    ctx.strokeRect(x + 1.5, y + 1.5, width - 3, height - 3);
    ctx.setLineDash([]);
  }

  // 6. Corner brackets.
  const arm = Math.min(width, height) * 0.18;
  ctx.strokeStyle = hexToRgba(neon, 0.8);
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (const [cx, cy, sx, sy] of [
    [x + 3, y + 3, 1, 1],
    [x + width - 3, y + 3, -1, 1],
    [x + width - 3, y + height - 3, -1, -1],
    [x + 3, y + height - 3, 1, -1],
  ] as const) {
    ctx.moveTo(cx + sx * arm, cy);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx, cy + sy * arm);
  }
  ctx.stroke();

  // 7. Glowing glyph projected in the middle of the pane.
  const pulse = 0.55 + 0.15 * Math.abs(Math.sin(nowMs / 500));
  ctx.save();
  ctx.shadowColor = neon;
  ctx.shadowBlur = 12;
  stampHazardGlyph(
    ctx, h,
    `600 ${Math.min(width, height) * 0.42}px system-ui, sans-serif`,
    hexToRgba("#eaffff", pulse)
  );
  ctx.restore();
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FLOOR_WASHES = ["#7a9c5a", "#5a87b0", "#c9973f", "#b5623a", "#8a6fae"];

function buildPaperFloor(w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  // Match the canvas backing scale for crisp ruling, but cap the bitmap at
  // ~12 MP so huge arenas don't eat memory for a static background.
  const scale = Math.max(1, Math.min(3, dpr, Math.sqrt(12_000_000 / (w * h))));
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  const ctx = c.getContext("2d")!;
  ctx.scale(scale, scale);

  // 1. Radial paper gradient (warm vignette).
  const grad = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.1,
    w / 2, h / 2, Math.max(w, h) * 0.8
  );
  grad.addColorStop(0, "#f2e7cc");
  grad.addColorStop(1, "#dac59c");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // 2. Faint blue graph ruling — ties the arena to the page around it.
  ctx.strokeStyle = "rgba(47, 91, 140, 0.08)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 26; x < w; x += 26) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
  }
  for (let y = 26; y < h; y += 26) {
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }
  ctx.stroke();

  // 3. Soft watercolor washes in muted greens/blues/ochres, scaled with area.
  const rand = mulberry32((w * 73856093) ^ (h * 19349663));
  const washes = Math.max(6, Math.round((w * h) / 70000));
  for (let i = 0; i < washes; i++) {
    const cx = 20 + rand() * (w - 40);
    const cy = 20 + rand() * (h - 40);
    const cr = 50 + rand() * 90;
    const color = FLOOR_WASHES[i % FLOOR_WASHES.length];
    const wash = ctx.createRadialGradient(cx, cy, cr * 0.15, cx, cy, cr);
    wash.addColorStop(0, hexToRgba(color, 0.09));
    wash.addColorStop(1, hexToRgba(color, 0));
    ctx.fillStyle = wash;
    ctx.beginPath();
    ctx.arc(cx, cy, cr, 0, Math.PI * 2);
    ctx.fill();
  }

  // 4. Ink/charcoal paper speckles (the original hand-drawn grain).
  ctx.globalAlpha = 0.05;
  ctx.strokeStyle = "#46371e";
  ctx.lineWidth = 1;
  for (let i = 0; i < 80; i++) {
    const fx = ((w * i * 17) % (w - 20)) + 10;
    const fy = ((h * i * 31) % (h - 20)) + 10;
    const len = 1.5 + (i % 3);
    const angle = ((i * 47) % 360) * (Math.PI / 180);
    ctx.beginPath();
    ctx.moveTo(fx, fy);
    ctx.lineTo(fx + Math.cos(angle) * len, fy + Math.sin(angle) * len);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // 5. Stamped dashed map border in the theme's stamp red.
  ctx.strokeStyle = "rgba(178, 59, 46, 0.22)";
  ctx.lineWidth = 1.5;
  ctx.setLineDash([12, 7]);
  ctx.strokeRect(8.5, 8.5, w - 17, h - 17);
  ctx.setLineDash([]);

  // 6. Compass rose in the bottom-right corner, when the map has room.
  if (w > 320 && h > 320) drawCompassRose(ctx, w - 54, h - 54, 30);

  return c;
}

function buildBattleCityFloor(w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  const scale = Math.max(1, Math.min(3, dpr, Math.sqrt(12_000_000 / (w * h))));
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  const ctx = c.getContext("2d")!;
  ctx.scale(scale, scale);

  // 1. Pitch-black base.
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, w, h);

  // 2. Faint 16px tile checker — gives the void a floor plane to sit on.
  ctx.fillStyle = "#0d0d0d";
  const tile = 16;
  for (let y = 0, row = 0; y < h; y += tile, row++) {
    for (let x = row % 2 === 0 ? 0 : tile; x < w; x += tile * 2) {
      ctx.fillRect(x, y, tile, tile);
    }
  }

  // 3. Pixel rubble: sparse 2px debris clusters, like leftover brick dust.
  const rand = mulberry32((w * 68041) ^ (h * 84631));
  const rubble = Math.max(12, Math.round((w * h) / 14000));
  ctx.fillStyle = "#1e1e1e";
  for (let i = 0; i < rubble; i++) {
    const cx = Math.floor(rand() * (w - 8)) + 4;
    const cy = Math.floor(rand() * (h - 8)) + 4;
    const n = 1 + Math.floor(rand() * 3);
    for (let k = 0; k < n; k++) {
      ctx.fillRect(cx + Math.floor(rand() * 6) - 3, cy + Math.floor(rand() * 6) - 3, 2, 2);
    }
  }

  // 4. Dark bezel frame, like the NES cabinet edge around the playfield.
  ctx.strokeStyle = "#2e2e2e";
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, w - 2, h - 2);
  ctx.strokeStyle = "#161616";
  ctx.strokeRect(3, 3, w - 6, h - 6);

  return c;
}

function buildRealisticFloor(w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  const scale = Math.max(1, Math.min(3, dpr, Math.sqrt(12_000_000 / (w * h))));
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  const ctx = c.getContext("2d")!;
  ctx.scale(scale, scale);

  // 1. Sandy base, brightest toward the top-left "sun".
  const grad = ctx.createRadialGradient(
    w * 0.42, h * 0.38, Math.min(w, h) * 0.15,
    w / 2, h / 2, Math.max(w, h) * 0.75
  );
  grad.addColorStop(0, "#94855f");
  grad.addColorStop(1, "#7a6c4e");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // 2. Blocky terrain-noise patches (the original look, baked in).
  ctx.globalAlpha = 0.06;
  const patch = 48;
  for (let x = 0; x < w; x += patch) {
    for (let y = 0; y < h; y += patch) {
      const v = ((x * 7 + y * 13) % 37) / 37;
      const shade = Math.floor(v * 40) - 20;
      ctx.fillStyle = `rgb(${139 + shade},${125 + shade},${94 + shade})`;
      ctx.fillRect(x, y, patch, patch);
    }
  }
  ctx.globalAlpha = 1;

  const rand = mulberry32((w * 40503) ^ (h * 96769));

  // 3. Faded tire ruts: pairs of parallel dashed curves.
  const tracks = Math.max(2, Math.round((w * h) / 450000));
  ctx.strokeStyle = "rgba(52, 44, 30, 0.11)";
  ctx.lineWidth = 3;
  ctx.setLineDash([14, 10]);
  for (let i = 0; i < tracks; i++) {
    const x1 = rand() * w;
    const y1 = rand() * h;
    const ang = rand() * Math.PI * 2;
    const len = 180 + rand() * 260;
    const bend = (rand() - 0.5) * 120;
    const x2 = x1 + Math.cos(ang) * len;
    const y2 = y1 + Math.sin(ang) * len;
    const mx = (x1 + x2) / 2 + Math.cos(ang + Math.PI / 2) * bend;
    const my = (y1 + y2) / 2 + Math.sin(ang + Math.PI / 2) * bend;
    const px = Math.cos(ang + Math.PI / 2) * 5;
    const py = Math.sin(ang + Math.PI / 2) * 5;
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(x1 + px * side, y1 + py * side);
      ctx.quadraticCurveTo(mx + px * side, my + py * side, x2 + px * side, y2 + py * side);
      ctx.stroke();
    }
  }
  ctx.setLineDash([]);

  // 4. Soft dirt splotches.
  const splotches = Math.max(5, Math.round((w * h) / 90000));
  for (let i = 0; i < splotches; i++) {
    const cx = rand() * w;
    const cy = rand() * h;
    const cr = 30 + rand() * 70;
    const g = ctx.createRadialGradient(cx, cy, cr * 0.1, cx, cy, cr);
    g.addColorStop(0, "rgba(74, 62, 40, 0.10)");
    g.addColorStop(1, "rgba(74, 62, 40, 0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, cr, 0, Math.PI * 2);
    ctx.fill();
  }

  // 5. Scattered rocks: cast shadow down-right, glint up-left.
  const rocks = Math.max(8, Math.round((w * h) / 45000));
  for (let i = 0; i < rocks; i++) {
    const cx = 8 + rand() * (w - 16);
    const cy = 8 + rand() * (h - 16);
    const cr = 1.5 + rand() * 3;
    ctx.fillStyle = "rgba(30, 25, 15, 0.28)";
    ctx.beginPath();
    ctx.ellipse(cx + cr * 0.5, cy + cr * 0.6, cr, cr * 0.75, 0, 0, Math.PI * 2);
    ctx.fill();
    const tone = Math.floor(rand() * 22);
    ctx.fillStyle = `rgb(${102 + tone},${95 + tone},${79 + tone})`;
    ctx.beginPath();
    ctx.ellipse(cx, cy, cr, cr * 0.8, rand() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(210, 200, 170, 0.35)";
    ctx.beginPath();
    ctx.ellipse(cx - cr * 0.3, cy - cr * 0.3, cr * 0.35, cr * 0.25, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // 6. Dry grass tufts for a hint of green.
  const tufts = Math.max(6, Math.round((w * h) / 60000));
  ctx.lineWidth = 1.2;
  for (let i = 0; i < tufts; i++) {
    const cx = 6 + rand() * (w - 12);
    const cy = 6 + rand() * (h - 12);
    ctx.strokeStyle = `rgba(96, 112, 60, ${0.35 + rand() * 0.2})`;
    for (let b = 0; b < 4; b++) {
      const a = -Math.PI / 2 + (b - 1.5) * 0.35 + (rand() - 0.5) * 0.15;
      const bladeLen = 4 + rand() * 4;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * bladeLen, cy + Math.sin(a) * bladeLen);
      ctx.stroke();
    }
  }

  // 7. Edge vignette pulls the eye toward the battlefield.
  const vg = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.35,
    w / 2, h / 2, Math.max(w, h) * 0.72
  );
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(30, 24, 12, 0.16)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, w, h);

  return c;
}

function drawCompassRose(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.globalAlpha = 0.4;

  ctx.strokeStyle = "#7e5a34";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([2, 4]);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.7, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  const point = (angle: number, len: number, halfW: number, color: string) => {
    ctx.save();
    ctx.rotate(angle);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(len, 0);
    ctx.lineTo(0, halfW);
    ctx.lineTo(-len * 0.25, 0);
    ctx.lineTo(0, -halfW);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  };
  // Cardinal points in stamp red, intercardinals smaller in ink blue.
  for (let i = 0; i < 4; i++) point((i * Math.PI) / 2 - Math.PI / 2, r * 0.92, r * 0.16, "#b23b2e");
  for (let i = 0; i < 4; i++) point((i * Math.PI) / 2 - Math.PI / 4, r * 0.55, r * 0.12, "#2f5b8c");

  ctx.fillStyle = "#f2e7cc";
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#352f25";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();
}

function buildModernFloor(w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  const scale = Math.max(1, Math.min(3, dpr, Math.sqrt(12_000_000 / (w * h))));
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  const ctx = c.getContext("2d")!;
  ctx.scale(scale, scale);

  // 1. Ambient pool of light: radial gradient from center (#0d1420) to edges (#05070d)
  const grad = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.1,
    w / 2, h / 2, Math.max(w, h) * 0.75
  );
  grad.addColorStop(0, "#0d1420");
  grad.addColorStop(1, "#05070d");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const rand = mulberry32((w * 51307) ^ (h * 72809));

  // 2. Floor plates: 256px panels outlined with faint white seams, a few lit
  // brighter. Offset half a plate from the 256px major grid — otherwise the
  // seams sit exactly under the grid lines and vanish.
  ctx.strokeStyle = "rgba(255, 255, 255, 0.03)";
  ctx.lineWidth = 1;
  for (let py = -128; py < h; py += 256) {
    for (let px = -128; px < w; px += 256) {
      ctx.strokeRect(px, py, 256, 256);
      if (rand() < 0.15) {
        ctx.fillStyle = "rgba(255, 255, 255, 0.02)";
        ctx.fillRect(px, py, 256, 256);
      }
    }
  }

  // 3. Two-tier grid
  // Minor lines every 64px (rgba(0,248,248,0.05)), major lines every 256px (rgba(0,248,248,0.10), 1.5px)
  ctx.beginPath();
  for (let x = 64; x < w; x += 64) {
    if (x % 256 !== 0) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
    }
  }
  for (let y = 64; y < h; y += 64) {
    if (y % 256 !== 0) {
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
    }
  }
  ctx.strokeStyle = "rgba(0, 248, 248, 0.05)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.beginPath();
  for (let x = 256; x < w; x += 256) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
  }
  for (let y = 256; y < h; y += 256) {
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }
  ctx.strokeStyle = "rgba(0, 248, 248, 0.10)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Glowing intersection dots on major crossings
  ctx.fillStyle = "rgba(0, 248, 248, 0.3)";
  for (let x = 256; x < w; x += 256) {
    for (let y = 256; y < h; y += 256) {
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // 4. Data decals: 2-3 large faint circles ("landing pads") and a few hazard-chevron strips near the arena border
  const decalsCount = Math.max(1, Math.round((w * h) / 360000));
  for (let d = 0; d < decalsCount; d++) {
    const cx = 100 + rand() * (w - 200);
    const cy = 100 + rand() * (h - 200);
    const r = 60 + rand() * 40;
    
    ctx.save();
    ctx.strokeStyle = "rgba(0, 248, 248, 0.04)";
    ctx.lineWidth = 1.5;
    
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.arc(cx, cy, r - 8, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.strokeStyle = "rgba(255, 255, 255, 0.03)";
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      ctx.moveTo(cx + Math.cos(angle) * (r - 14), cy + Math.sin(angle) * (r - 14));
      ctx.lineTo(cx + Math.cos(angle) * (r - 4), cy + Math.sin(angle) * (r - 4));
    }
    ctx.stroke();
    ctx.restore();
  }

  const borderOffset = 40;
  const drawChevronStrip = (x: number, y: number, length: number, horizontal: boolean) => {
    ctx.save();
    ctx.fillStyle = "rgba(0, 248, 248, 0.05)";
    ctx.strokeStyle = "rgba(255, 255, 255, 0.03)";
    ctx.lineWidth = 1.2;
    
    const step = 15;
    for (let offset = 0; offset < length; offset += step) {
      const cx = horizontal ? x + offset : x;
      const cy = horizontal ? y : y + offset;
      
      ctx.beginPath();
      if (horizontal) {
        ctx.moveTo(cx - 5, cy - 5);
        ctx.lineTo(cx, cy);
        ctx.lineTo(cx - 5, cy + 5);
      } else {
        ctx.moveTo(cx - 5, cy - 5);
        ctx.lineTo(cx, cy);
        ctx.lineTo(cx + 5, cy - 5);
      }
      ctx.stroke();
    }
    ctx.restore();
  };

  if (w > 512) {
    drawChevronStrip(128, borderOffset, w - 256, true);
    drawChevronStrip(128, h - borderOffset, w - 256, true);
  }
  if (h > 512) {
    drawChevronStrip(borderOffset, 128, h - 256, false);
    drawChevronStrip(w - borderOffset, 128, h - 256, false);
  }

  // 5. Edge falloff — kept gentle (≤0.35) so tanks fighting along the border
  // walls stay clearly visible; the ambient pool already darkens the edges.
  const edgeGrad = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.55,
    w / 2, h / 2, Math.max(w, h) * 0.75
  );
  edgeGrad.addColorStop(0, "rgba(0,0,0,0)");
  edgeGrad.addColorStop(1, "rgba(5, 7, 13, 0.35)");
  ctx.fillStyle = edgeGrad;
  ctx.fillRect(0, 0, w, h);

  return c;
}
